
import { LayoutGrid, List, Search, Tag, X } from "lucide-react";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { SearchBar } from "./SearchBar";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";

interface DrawingHistoryHeaderProps {
  viewMode: "grid" | "list";
  setViewMode: (mode: "grid" | "list") => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  selectedTags: string[];
  availableTags: string[];
  onToggleTag: (tag: string) => void;
}

export function DrawingHistoryHeader({
  viewMode,
  setViewMode,
  searchQuery,
  setSearchQuery,
  selectedTags,
  availableTags,
  onToggleTag,
}: DrawingHistoryHeaderProps) {
  // Ensure we have valid arrays
  const safeSelectedTags = Array.isArray(selectedTags) ? selectedTags : [];
  const safeAvailableTags = Array.isArray(availableTags) ? availableTags : [];
  
  return (
    <div className="flex-none border-b border-border/40 backdrop-blur-sm">
      <div className="px-6 py-5 bg-background/80">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-semibold tracking-tight bg-gradient-to-br from-foreground to-foreground/70 bg-clip-text text-transparent">
              Drawing History
            </h2>
            <ToggleGroup 
              type="single" 
              value={viewMode} 
              onValueChange={(value) => value && setViewMode(value as "grid" | "list")}
              className="border border-border/50 rounded-lg overflow-hidden bg-muted/30"
            >
              <ToggleGroupItem 
                value="grid" 
                aria-label="Grid view"
                className={cn(
                  "data-[state=on]:bg-white data-[state=on]:text-primary", 
                  "data-[state=off]:text-muted-foreground/70 h-8 w-10"
                )}
              >
                <LayoutGrid className="h-4 w-4" />
              </ToggleGroupItem>
              <ToggleGroupItem 
                value="list" 
                aria-label="List view"
                className={cn(
                  "data-[state=on]:bg-white data-[state=on]:text-primary", 
                  "data-[state=off]:text-muted-foreground/70 h-8 w-10 border-l border-border/50"
                )}
              >
                <List className="h-4 w-4" />
              </ToggleGroupItem>
            </ToggleGroup>
          </div>
          
          <div className="space-y-3">
            <SearchBar
              value={searchQuery}
              onChange={setSearchQuery}
              selectedTags={safeSelectedTags}
              availableTags={safeAvailableTags}
              onToggleTag={onToggleTag}
            />

            {/* Selected Tags Display */}
            {safeSelectedTags.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                <div className="flex items-center mr-1">
                  <Tag className="h-3.5 w-3.5 text-muted-foreground mr-1" />
                  <span className="text-xs text-muted-foreground">Filters:</span>
                </div>
                {safeSelectedTags.map(tag => (
                  <Badge 
                    key={tag}
                    variant="secondary" 
                    className="text-xs bg-primary/10 text-primary hover:bg-primary/20 border-primary/20 flex items-center gap-1"
                  >
                    {tag}
                    <X 
                      className="h-3 w-3 cursor-pointer hover:text-destructive" 
                      onClick={() => onToggleTag(tag)}
                    />
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
